import { useEffect } from "react";
import { toast } from "sonner";
import { AnimatePresence, steps } from "framer-motion";
import { useOnboarding } from "@/context/OnboardingContext";
import WebsiteOnboardingStep from "@/components/onboarding/steps/WebsiteOnboardingStep";
import ContentIdeaSelectionStep from "@/components/onboarding/steps/ContentIdeaSelectionStep";
import GeneratingFirstPieceOfContentStep from "@/components/onboarding/steps/GeneratingFirstPieceOfContentStep";
import WordPressIntegrationStep from "@/components/onboarding/steps/WordPressIntegrationStep";
import PublishingPreferencesStep from "@/components/onboarding/steps/PublishingPreferencesStep";
import OnboardingCompletionStep from "@/components/onboarding/steps/OnboardingCompletionStep";
import EmailVerificationStep from "@/components/onboarding/steps/EmailVerificationStep";
import SignupDialog from "@/components/dialogs/SignupDialog";
import { transferDataToDatabase } from "@/api/onboardingImport";
import useOnboardingSetupAndInitialization from "@/hooks/useOnboardingSetupAndInitialization";

const PostOnboarding = () => {
  const { state, setState, setupStarted } = useOnboarding();
  const { handleStartSetup1 } = useOnboardingSetupAndInitialization();

  const stepComponents: Record<string | number, JSX.Element> = {
    1: <WebsiteOnboardingStep />,
    2: <ContentIdeaSelectionStep />,
    3: <GeneratingFirstPieceOfContentStep />,
    4: <WordPressIntegrationStep />,
    5: <PublishingPreferencesStep />,
    6: <OnboardingCompletionStep />,
    7: <OnboardingCompletionStep />,
    "email-verification": <EmailVerificationStep />,
  };

  // Handle URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get("url");
    const hashValue = (window.location.hash || "").replace(/^#/, "").trim();
    const stepParam = params.get("step");
    const transferParam = params.get("transfer");
    const onboardingParam = params.get("onboarding");

    // Handle data transfer after email verification
    if (transferParam === "true" && onboardingParam === "complete") {
      const userId = localStorage.getItem("pending_user_id");
      if (userId) {
        console.log("Auto-transferring data after email verification for user:", userId);
        transferDataToDatabase(userId).catch((error) => {
          console.error("Error auto-transferring data:", error);
          toast.error(
            "Error completing setup: " +
              (error.message || "Failed to complete setup. Please try again.")
          );
        });
      }
    }

    // Handle website URL parameter or hash domain
    const candidate = urlParam || (hashValue && !hashValue.includes("=/") ? hashValue : "");
    if (candidate) {
      // Decode once, then decode again if still encoded; then normalise
      try {
        let decoded = decodeURIComponent(candidate);
        if (decoded.includes('%')) {
          try { decoded = decodeURIComponent(decoded); } catch {}
        }
        // Remove accidental protocol duplication like https://https%3A%2F%2F...
        decoded = decoded.replace(/^https?:\/\/https?:\/\//, 'https://');
        decoded = decoded.replace(/^https?:\/\/https%3A%2F%2F/, 'https://');
        if (!/^https?:\/\//i.test(decoded)) {
          decoded = 'https://' + decoded;
        }
        // Keep only the origin (base site URL)
        try {
          const u = new URL(decoded);
          decoded = u.origin;
        } catch {}
        localStorage.setItem("onboardingWebsite", decoded);
        setState((prev) => ({ ...prev, websiteUrl: decoded }));
      } catch {
        // Fallback: store as-is with protocol prefix
        const formattedUrl = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;
        localStorage.setItem("onboardingWebsite", formattedUrl);
        setState((prev) => ({ ...prev, websiteUrl: formattedUrl }));
      }
    }

    // Handle step parameter
    if (stepParam) {
      // Map step parameter to step number
      const stepMap: Record<string, number> = {
        ideas: 2,
        "post-draft": 3,
        scheduling: 4,
        auth: 5,
        publish: 5,
        preferences: 5,
        integration: 7, // Completion
      };

      const stepNumber = stepMap[stepParam];

      if (stepNumber) {
        console.log(`Navigating to step ${stepNumber} based on URL parameter`);

        // Only allow navigation to steps that are implemented
        if (stepNumber <= 5) {
          // Updated to include scheduling step
          setState((prev) => ({ ...prev, step: stepNumber }));

          // If we're skipping to a later step, we need to ensure previous steps are completed
          if (stepNumber > 1) {
            // Mark setup as started to prevent automatic start
            setupStarted.current = true;

            // Set progress to 100% for previous steps
            setState((prev) => ({
              ...prev,
              progress: 100,
              currentStepIndex: steps.length - 1,
            }));
          }
        } else {
          // For steps that are coming soon, show a toast message
          toast.info(
            "Coming Soon: This feature is coming soon! Starting from the beginning."
          );
        }
      }
    }
  }, []); // Only run once on mount

  // Handle initial setup
  useEffect(() => {
    // Only start setup if we're on step 1, haven't started yet, and URL isn't default
    const hasRealWebsite = state.websiteUrl && !state.websiteUrl.includes("example.com");
    if (state.step === 1 && !setupStarted.current && hasRealWebsite) {
      console.log("Starting onboarding process for website:", state.websiteUrl);
      setupStarted.current = true;
      handleStartSetup1();
    }
  }, [state.step, state.websiteUrl]);

  return (
    <AnimatePresence>
      {stepComponents[state.step] ? (
        <div key={`step-${state.step}`}>
          {stepComponents[state.step]}
        </div>
      ) : null}

      <SignupDialog key="signup-dialog" />
    </AnimatePresence>
  );
};

export default PostOnboarding;
